import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import type { LocalizedText, StringTable } from '#contract'
import { assert, createLogger } from '#shared'

export interface I18n {
  /** Translates a key with its `{name}` variables, or passes raw text through. */
  readonly t: (text: LocalizedText) => string
  /** The BCP 47 locale numbers, dates and weekday names are formatted in. */
  readonly locale: string
}

const log = createLogger('i18n')

/**
 * Later tables override earlier ones, so content can reword a UI string. English formats
 * dates the European way (`en-GB`: "20 Oct, 14:02"), like the game's 24-hour clock.
 */
export function createI18n(tables: readonly StringTable[], locale = 'en-GB'): I18n {
  const strings: Record<string, string> = {}
  for (const table of tables) Object.assign(strings, table)
  const warned = new Set<string>()

  const t = (text: LocalizedText): string => {
    if ('raw' in text) return text.raw
    const template = strings[text.key]
    if (template === undefined) {
      if (import.meta.env.DEV && !warned.has(text.key)) {
        warned.add(text.key)
        log.warn(`Missing string "${text.key}"`)
      }
      return text.key
    }
    const { vars } = text
    if (vars === undefined) return template
    return template.replace(/\{(\w+)\}/g, (match, name: string) => {
      const value = vars[name]
      if (value === undefined) return match
      return typeof value === 'object' ? t(value) : String(value)
    })
  }
  return { t, locale }
}

export const I18nContext = createContext<I18n | null>(null)

export function useT(): I18n['t'] {
  return useI18n().t
}

export function useLocale(): string {
  return useI18n().locale
}

function useI18n(): I18n {
  const i18n = useContext(I18nContext)
  assert(i18n, 'useT() needs an <I18nContext.Provider> above it')
  return i18n
}
